import { useCallback, useId } from "react";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import { type SeverityColor, SeverityColorDot, severityColorLabels, severityColors } from "./severity.utils";

export type SeveritySelectProps = {
  severityColor: SeverityColor;
  onChange: (severityColor: SeverityColor) => void;
  fullwidth?: boolean;
};

const SeveritySelect = ({ severityColor, onChange, fullwidth }: SeveritySelectProps) => {
  const { t } = useTranslation(["common"]);
  const uniqueId = useId();
  const labelId = `${uniqueId}-severity-color-select-label`;
  const selectId = `${uniqueId}-severity-color-select`;
  const severityColorSelectLabel = t("common:color_label");

  const handleChange = useCallback(
    (event: SelectChangeEvent) => {
      onChange(event.target.value as SeverityColor);
    },
    [onChange],
  );

  const renderValue = useCallback((selectedSeverityColor: unknown) => {
    const selectedSeverityColorValue = selectedSeverityColor as SeverityColor;

    return (
      <Stack direction="row" spacing={1} alignItems="center" component="span">
        <SeverityColorDot severityColor={selectedSeverityColorValue} />
        <Typography>{severityColorLabels[selectedSeverityColorValue]}</Typography>
      </Stack>
    );
  }, []);

  return (
    <FormControl fullWidth={fullwidth} size="small">
      <InputLabel id={labelId}>{severityColorSelectLabel}</InputLabel>
      <Select
        labelId={labelId}
        id={selectId}
        value={severityColor}
        label={severityColorSelectLabel}
        onChange={handleChange}
        renderValue={renderValue}
        sx={{
          "& .MuiSelect-select": {
            alignItems: "center",
            display: "flex",
          },
        }}
      >
        {severityColors.map((option) => (
          <MenuItem key={option} value={option}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <SeverityColorDot severityColor={option} />
            </ListItemIcon>
            <ListItemText primary={severityColorLabels[option]} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default SeveritySelect;
